import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';

import useViewPermission from '../../../../services/contributors/hooks/useViewPermission';
import { useUploadPermission } from '../../../../services/contributors';
import { PERMISSION_NONE } from '../../../../../../constants/contributors';
import Button from '../../../../../../components/global/Button';
import { getAsset } from '../../../../../../util/assets';
import { ALBUM_TYPE } from '../../../../../../constants/albums';
import getConfig from '../../../../../../util/config';
import inIframe from '../../../../../../util/in-iframe';
import ContextualString from '../../../../../../components/contextual/ContextualString';
import Badge from '../../../../../../components/global/Badge';

const propTypes = {
  entity: PropTypes.shape({
    id: PropTypes.string,
    is_web_gallery: PropTypes.bool,
    joins_enabled: PropTypes.bool,
  }),
  authenticated: PropTypes.bool,
  onViewPhotos: PropTypes.func,
  onFindMyMatches: PropTypes.func,
  onUpload: PropTypes.func,
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
};

const ContributorActions = ({
  authenticated,
  onViewPhotos,
  onFindMyMatches,
  onUpload,
  history,
  entity,
}) => {
  const autoContributorSettings = entity?.auto_contributor_settings;

  const { isAllowed: isAllowedToView } = useViewPermission({
    entityId: entity?.id,
    checkOwnership: true,
  });

  const { isAllowed: isAllowedToUpload } = useUploadPermission({
    entityId: entity?.id,
    checkOwnership: true,
  });

  const canView =
    entity?.is_web_gallery ||
    isAllowedToView ||
    autoContributorSettings?.view_access !== PERMISSION_NONE;

  const canUpload =
    (authenticated && isAllowedToUpload) ||
    (!!autoContributorSettings?.upload_access &&
      autoContributorSettings?.upload_access !== PERMISSION_NONE);
  const canOnboard = entity?.joins_enabled;

  const account = entity?.account ?? entity?.creator;

  const onOnboardEntity = () => {
    onFindMyMatches?.();
    if (inIframe()) {
      window.open(
        `${getConfig().app.baseUrl}/discover/album-folder/${
          entity?.root_album_folder?.id ?? entity?.id
        }`,
        '_blank',
      );
    } else {
      history.push(
        `/discover/album-folder/${entity?.root_album_folder?.id ?? entity?.id}`,
      );
    }
  };

  const onUploadPhotos = () => {
    () => {
      onUpload?.();
      if (inIframe()) {
        window.open(
          `${getConfig().app.baseUrl}/contributors/upload/${entity.id}`,
          '_blank',
        );
      } else {
        history.push(`/contributors/upload/${entity.id}`);
      }
    };
  };

  useEffect(() => {
    if (!entity?.id) {
      return;
    }

    if (!authenticated && entity.album_type === ALBUM_TYPE.SHUTTERBUG) {
      onViewPhotos?.();
      return;
    }

    if (!canUpload && !canOnboard) {
      onViewPhotos?.();
      return;
    }

    if (!canUpload && !canView) {
      onOnboardEntity();
      return;
    }

    if (!canView && !canOnboard) {
      onUploadPhotos();
    }
  }, [entity?.id]);

  if (!entity) {
    return null;
  }

  return (
    <div className="contributor-actions-modal-content">
      <p className="contributor-actions-modal-welcome">Welcome To</p>
      <h3 className="contributor-actions-modal-title">{entity.name}</h3>

      {account.username && (
        <p className="text-xs d-flex align-items-center">
          by{' '}
          <Button btnStyle="link" to={`/@${account.username}`} className="ml-1">
            @{account.username}
          </Button>{' '}
        </p>
      )}

      <hr />

      <div className="contributor-actions-modal-actions">
        {canView && (
          <div className="contributor-actions-modal-action">
            <img src={getAsset('/waldo-view-photos.png')} alt="View Photos" />
            <div className="contributor-actions-modal-action-content">
              <p className="text-extra-bold">
                Gallery viewing
                <br />
                (manual search)
              </p>
              <p className="text-xs text-light">
                View all the photos and search for yours.
              </p>
              <Button
                btnBlock
                onClick={() => {
                  onViewPhotos?.();
                }}
              >
                Search gallery
              </Button>
            </div>
          </div>
        )}
        {canOnboard && (
          <div className="contributor-actions-modal-action">
            <Badge badgeStyle="yellow" badgeSize="xs" className="text-bold">
              POPULAR!
            </Badge>
            <img src={getAsset('/waldo-way.png')} alt="Find my photos" />
            <div className="contributor-actions-modal-action-content">
              <p className="text-extra-bold">
                Gallery viewing
                <br />
                (with auto-tagging)
              </p>
              <p className="text-xs text-light">
                <ContextualString
                  id="waldoFindPhotosYouAreInTitle"
                  type={entity.album_type}
                  subtype={entity.album_subtype}
                  defaultMessage="Enjoy the gallery but let Waldo do the \n hunting and pecking! All your \n photos sent via text alert when found."
                />
              </p>
              <Button btnBlock onClick={onOnboardEntity}>
                Activate auto-tagging
              </Button>
            </div>
          </div>
        )}
        {canUpload && (
          <div className="contributor-actions-modal-action">
            <img
              src={getAsset('/upload-photos-illustration.svg')}
              alt="Upload Photos"
            />
            <div className="contributor-actions-modal-action-content">
              <p className="text-extra-bold">
                Snapped photos? <br /> Add yours here!
              </p>
              <p className="text-xs text-light">
                Upload photos of the event and share them with everyone!
              </p>
              <Button btnBlock onClick={onUploadPhotos}>
                Upload now
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

ContributorActions.propTypes = propTypes;

const mapStateToProps = ({ auth }) => {
  return {
    authenticated: auth.authenticated,
  };
};

export { ContributorActions as ContributorActionsComponent };

export default compose(
  withRouter,
  connect(mapStateToProps),
)(ContributorActions);
